// Taste-badge stickers — LLM-personalized collectibles ("Korean Thriller
// Connoisseur"), slapped onto the sheet with a staggered sticker-drop. Falls
// back to the deterministic archetypes when no LLM set exists (public pages of
// profiles that never generated, LLM down). Shared by solo + public sheets.

import { useMemo } from "react";
import { Kicker, H3 } from "./sheet.tsx";
import { computeArchetypes } from "../lib/archetypes.ts";
import type { TasteBadge } from "../lib/api.ts";
import type { DistilledProfile } from "../lib/types.ts";
import { IS_TEST } from "../lib/env.ts";

const TONES = [
  { border: "border-red-500", text: "text-red-700", bg: "bg-red-100" },
  { border: "border-gold-600", text: "text-gold-600", bg: "bg-gold-100" },
  { border: "border-clay-500", text: "text-clay-500", bg: "bg-paper-50" },
  { border: "border-ink-900", text: "text-ink-900", bg: "bg-paper-100" },
];
const TILTS = [-2.2, 1.6, -1.2, 2.4];

export function TasteBadges({
  profile,
  badges,
  loading = false,
  onRegenerate,
  heading = "Your type",
}: {
  profile: DistilledProfile;
  /** LLM-personalized set; when absent the deterministic archetypes print. */
  badges?: TasteBadge[] | null;
  loading?: boolean;
  /** Members only: mint a fresh set. */
  onRegenerate?: () => void;
  heading?: string;
}) {
  // Fallback: deterministic archetypes shaped like badges.
  const fallback = useMemo(
    () => computeArchetypes(profile).slice(0, 4).map((a) => ({ emoji: a.emoji, title: a.title, blurb: a.blurb })),
    [profile],
  );
  const shown = badges && badges.length ? badges : fallback;
  const setKey = shown.map((b) => b.title).join("|"); // re-slap on new set

  return (
    <section data-testid="section-archetypes" className="px-6 py-7 sm:px-10">
      <Kicker>{heading}</Kicker>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <H3>Taste stickers</H3>
        {onRegenerate && !loading && badges && badges.length > 0 && (
          <button
            type="button"
            data-testid="action-regenerate-badges"
            onClick={onRegenerate}
            className="font-mono text-[11px] font-bold uppercase tracking-stamp text-ink-400 underline underline-offset-2 hover:text-red-600"
          >
            Mint a new set ↻
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="badges-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[74px] w-[210px] rounded-xl shimmer" />
          ))}
        </div>
      ) : (
        <div key={setKey} className="mt-4 flex flex-wrap gap-3" data-testid="badge-stickers">
          {shown.map((b, i) => {
            const tone = TONES[i % TONES.length];
            return (
              <span key={b.title} style={IS_TEST ? undefined : { transform: `rotate(${TILTS[i % TILTS.length]}deg)` }}>
                <span
                  className={`${IS_TEST ? "" : "stickin"} flex max-w-[240px] items-center gap-3 rounded-xl border-2 ${tone.border} ${tone.bg} px-3.5 py-2.5 shadow-[3px_4px_0_rgba(31,17,20,0.10)]`}
                  style={IS_TEST ? undefined : { animationDelay: `${i * 0.14}s`, display: "flex" }}
                  data-testid="badge-sticker"
                >
                  <span className="text-[26px] leading-none" aria-hidden>
                    {b.emoji}
                  </span>
                  <span className="min-w-0">
                    <span className={`block font-display text-[15px] font-extrabold leading-tight tracking-tightish ${tone.text}`}>
                      {b.title}
                    </span>
                    {b.blurb && <span className="block text-[11.5px] leading-snug text-ink-500">{b.blurb}</span>}
                  </span>
                </span>
              </span>
            );
          })}
        </div>
      )}
    </section>
  );
}
